import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from "@mui/icons-material";
import { Grid, IconButton, Stack, Typography } from "@mui/material";
import isEqual from "lodash/isEqual";
import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import AsyncLoadingButton from "../../../components/asyncLoadingButton/AsyncLoadingButton";
import { swapArr } from "../../../misc/utils";
import { sortProductOptions } from "../../../sharedComponents/products/sortProductOptions";
import type { AdminProductOptionResponse } from "../productsApi";
import { useProductOptions } from "../useProductOptions";

const ProductOptionsOrder = () => {
  const { productId, options } = useOutletContext<{
    readonly productId: string;
    readonly options: AdminProductOptionResponse[];
  }>();
  const { reorderOptions } = useProductOptions(productId);
  const savedOrder = sortProductOptions(options);
  const [orderedOptions, setOrderedOptions] = useState(savedOrder);

  useEffect(() => {
    setOrderedOptions(sortProductOptions(options));
  }, [options]);

  const moveOption = (idx: number, targetIdx: number) => {
    setOrderedOptions((current) => swapArr(current, idx, targetIdx));
  };

  const hasChanges = !isEqual(
    orderedOptions.map((o) => o.id),
    savedOrder.map((o) => o.id),
  );

  if (options.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        Ten produkt nie ma żadnych opcji.
      </Typography>
    );
  }

  return (
    <Grid container spacing={2} data-testid="product-options-order">
      <Grid size={12}>
        <Typography variant="h6">Kolejność opcji</Typography>
      </Grid>
      <Grid size={{ xs: 12, lg: 6 }} container>
        {orderedOptions.map((option, idx) => (
          <Grid
            key={option.id}
            size={12}
            container
            sx={{ alignItems: "center" }}
            data-testid="product-option-order-row"
          >
            <Grid size={8}>
              <Typography variant="body2">{option.name}</Typography>
            </Grid>
            <Grid size={4}>
              <Stack direction="row">
                <IconButton
                  aria-label="Przesuń w górę"
                  size="small"
                  disabled={idx === 0}
                  onClick={() => moveOption(idx, idx - 1)}
                >
                  <KeyboardArrowUpIcon />
                </IconButton>
                <IconButton
                  aria-label="Przesuń w dół"
                  size="small"
                  disabled={idx === orderedOptions.length - 1}
                  onClick={() => moveOption(idx, idx + 1)}
                >
                  <KeyboardArrowDownIcon />
                </IconButton>
              </Stack>
            </Grid>
          </Grid>
        ))}
      </Grid>
      <Grid
        size={12}
        sx={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <AsyncLoadingButton disabled={!hasChanges} onClick={() => reorderOptions(orderedOptions.map((o) => o.id))}>
          Zapisz
        </AsyncLoadingButton>
      </Grid>
    </Grid>
  );
};

export default ProductOptionsOrder;
